import { API_BASE_URL } from "../../../../environment";
import { getAuthHeaders, handleAuthError } from "../../../../utils/authUtils";
import { requestExportByQuery } from "../../Dashboard/dashboardApi";

export type SummaryEntityKey = "leads" | "pipelines" | "deals";

export type SummaryPeriod = "by_week" | "by_month" | "by_year";

export const SUMMARY_ENTITY_KEYS: SummaryEntityKey[] = ["leads", "pipelines", "deals"];

export const SUMMARY_PERIODS: SummaryPeriod[] = ["by_week", "by_month", "by_year"];

export interface SummaryTopEntity {
  id: number | null;
  name: string;
  companyName: string;
  companyLogoUrl: string | null;
  assignedUserName: string;
  assignedUserEmail: string;
  assignedUserAvatarUrl: string | null;
  status: string;
  source: string;
  stage: string;
  estimatedValue: number;
  grandTotal: number | null;
  priority: string;
  currency: string;
}

export interface SummaryStageStat {
  stage: string;
  count: number;
  amount: number;
}

export interface SummaryProductStat {
  name: string;
  imageUrl: string | null;
  count: number;
  amount: number;
}

export interface SummaryDatePoint {
  date: string;
  count: number;
  amount: number;
}

export interface SummaryReport {
  top: Record<SummaryEntityKey, SummaryTopEntity[]>;
  stages: Record<SummaryEntityKey, SummaryStageStat[]>;
  products: Record<SummaryEntityKey, SummaryProductStat[]>;
  timeline: Record<SummaryPeriod, Record<SummaryEntityKey, SummaryDatePoint[]>>;
}

export interface SummaryReportQuery {
  stageIds: string[];
  statuses: string[];
  reasonIds: string[];
  teamIds: string[];
  userIds: string[];
  sourceIds: string[];
  priorities: string[];
  productIds: string[];
  currencyCodes: string[];
  startDate: string | null;
  endDate: string | null;
  salesUserId: number | null;
}

const ENTITY_ID_FIELDS: Record<SummaryEntityKey, { id: string; name: string; status: string; source: string }> = {
  leads: { id: "lead_id", name: "lead_name", status: "lead_status", source: "lead_source" },
  pipelines: { id: "pipeline_id", name: "pipeline_name", status: "pipeline_status", source: "pipeline_source" },
  deals: { id: "deal_id", name: "deal_name", status: "deal_status", source: "deal_source" },
};

const PERIOD_DATE_FIELDS: Record<SummaryPeriod, string> = {
  by_week: "week",
  by_month: "month",
  by_year: "year",
};

const toNumber = (value: unknown) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const toOptionalNumber = (value: unknown) => {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const toText = (value: unknown) => (value === null || value === undefined ? "" : String(value));

const asArray = (value: unknown): any[] => (Array.isArray(value) ? value : []);

const byEntity = <T,>(raw: any, normalize: (item: any, key: SummaryEntityKey) => T): Record<SummaryEntityKey, T[]> =>
  SUMMARY_ENTITY_KEYS.reduce(
    (result, key) => ({ ...result, [key]: asArray(raw?.[key]).map((item) => normalize(item, key)) }),
    {} as Record<SummaryEntityKey, T[]>
  );

const normalizeTopEntity = (item: any, key: SummaryEntityKey): SummaryTopEntity => {
  const fields = ENTITY_ID_FIELDS[key];
  return {
    id: toOptionalNumber(item?.[fields.id] ?? item?.id),
    name: toText(item?.[fields.name] ?? item?.name ?? item?.title),
    companyName: toText(item?.company?.name),
    companyLogoUrl: item?.company?.company_logo_url || null,
    assignedUserName: toText(item?.assigned_user?.full_name),
    assignedUserEmail: toText(item?.assigned_user?.user_email),
    assignedUserAvatarUrl: item?.assigned_user?.avatar_url || null,
    status: toText(item?.[fields.status] ?? item?.status),
    source: toText(item?.[fields.source] ?? item?.source),
    stage: toText(item?.stage),
    estimatedValue: toNumber(item?.estimated_value),
    grandTotal: toOptionalNumber(item?.grand_total),
    priority: toText(item?.priority),
    currency: toText(item?.currency ?? item?.currency_code),
  };
};

const normalizeStage = (item: any): SummaryStageStat => ({
  stage: toText(item?.stage) || "No Stage",
  count: toNumber(item?.count),
  amount: toNumber(item?.amount),
});

const normalizeProduct = (item: any): SummaryProductStat => ({
  name: toText(item?.product_name),
  imageUrl: item?.product_primary_url || null,
  count: toNumber(item?.count),
  amount: toNumber(item?.amount),
});

export const normalizeSummaryReport = (raw: any): SummaryReport => ({
  top: byEntity(raw?.top_5_lifecycle_entities_by_estimated_value, normalizeTopEntity),
  stages: byEntity(raw?.lifecycle_entity_count_and_amount_by_stage, normalizeStage),
  products: byEntity(raw?.product_n_service_sales_by_lifecycle_entity, normalizeProduct),
  timeline: SUMMARY_PERIODS.reduce(
    (result, period) => ({
      ...result,
      [period]: byEntity(raw?.lifecycle_entity_stats_by_date?.[period], (item: any) => ({
        date: toText(item?.[PERIOD_DATE_FIELDS[period]] ?? item?.date),
        count: toNumber(item?.count),
        amount: toNumber(item?.amount),
      })),
    }),
    {} as SummaryReport["timeline"]
  ),
});

const MULTI_PARAMS: [keyof SummaryReportQuery, string][] = [
  ["stageIds", "lifecycle_type_stage_id"],
  ["statuses", "status"],
  ["reasonIds", "lifecycle_status_reason_id"],
  ["teamIds", "assigned_team_id"],
  ["userIds", "assigned_user_id"],
  ["sourceIds", "lifecycle_source_id"],
  ["priorities", "priority"],
  ["productIds", "lifecycle_product_n_service_id"],
  ["currencyCodes", "currency_code"],
];

export const buildSummaryReportQuery = (tenantId: string | number, query: SummaryReportQuery) => {
  const params = new URLSearchParams();
  params.append("tenant_id", String(tenantId));
  MULTI_PARAMS.forEach(([key, param]) => {
    const values = Array.from(new Set((query[key] as string[]).map((value) => value.trim()).filter(Boolean)));
    if (values.length) params.append(param, values.join(","));
  });
  if (query.startDate) params.append("start_date", query.startDate);
  if (query.endDate) params.append("end_date", query.endDate);
  if (query.salesUserId) params.append("sales_user_id", String(query.salesUserId));
  return params.toString().replace(/%2C/gi, ",");
};

export const fetchSummaryReport = async (
  tenantId: string | number,
  query: SummaryReportQuery,
  signal?: AbortSignal
): Promise<SummaryReport> => {
  const response = await fetch(
    `${API_BASE_URL}/dashboard/summary-report/?${buildSummaryReportQuery(tenantId, query)}`,
    { headers: getAuthHeaders(), signal }
  );
  if (!response.ok) {
    if (response.status === 401 || response.status === 403) {
      handleAuthError({ response: { status: response.status } });
    }
    const payload = await response.json().catch(() => null);
    const message = payload?.detail ?? payload?.error ?? payload?.message;
    throw new Error(message ? String(message) : `Failed to load summary report (${response.status})`);
  }
  return normalizeSummaryReport(await response.json());
};

export const exportSummaryReportExcel = (tenantId: string | number, query: SummaryReportQuery) =>
  requestExportByQuery("/dashboard/summary-report/export/", buildSummaryReportQuery(tenantId, query), "summary report");
